/**
 * TerraDraw integration for MapLibre.
 *
 * Tools provided:
 *  - Measure distance / area using TerraDraw linestring/polygon modes.
 *    A popup follows the cursor and shows the current total distance or area
 *    while drawing.
 *  - Sketch a parcel polygon. Each vertex is labelled with the segment length
 *    from the previous vertex and the relative elevation to the first vertex.
 */
(function() {
  'use strict';

  const maplibregl = window.maplibregl;
  const turf = window.turf;

  // ── Lazy loading UMD-библиотеки (A2: -312KB из блокирующих скриптов) ──
  let terradrawLoadPromise = null;
  function ensureTerraDrawLoaded() {
    if (window.MaplibreTerradrawControl) return Promise.resolve();
    if (!terradrawLoadPromise) {
      terradrawLoadPromise = new Promise(function(resolve, reject) {
        const css = document.createElement('link');
        css.rel = 'stylesheet';
        css.href = '/vendor/terradraw/maplibre-gl-terradraw.min.css';
        document.head.appendChild(css);
        const sc = document.createElement('script');
        sc.src = '/vendor/terradraw/maplibre-gl-terradraw.umd.min.js';
        sc.async = true;
        sc.onload = function() { resolve(); };
        sc.onerror = function() { terradrawLoadPromise = null; reject(new Error('TerraDraw script failed to load')); };
        document.head.appendChild(sc);
      });
    }
    return terradrawLoadPromise;
  }

  const TERRAIN_URL = 'https://s3.amazonaws.com/elevation-tiles-prod/terrarium/{z}/{x}/{y}.png';
  const SKETCH_LABELS_SOURCE = 'sketch-labels-source';
  const SKETCH_LABELS_LAYER = 'sketch-labels-layer';

  let measureControl = null;
  let sketchControl = null;
  let measurePopup = null;
  let measureAdded = false;
  let sketchAdded = false;

  /* ------------------------------------------------------------------ */
  /* Helpers                                                            */
  /* ------------------------------------------------------------------ */

  function getMap() {
    return window.map || null;
  }

  function getTurf() {
    return window.turf || null;
  }

  function formatDistance(km) {
    if (km < 1) {
      return (km * 1000).toFixed(1) + ' м';
    }
    return km.toFixed(2) + ' км';
  }

  function formatArea(m2) {
    if (m2 >= 10000) {
      return (m2 / 10000).toFixed(2) + ' га';
    }
    return m2.toFixed(1) + ' м²';
  }

  function segmentDistance(a, b) {
    const turf = getTurf();
    if (turf && turf.distance) {
      return turf.distance(a, b, { units: 'meters' });
    }
    const R = 6371000;
    const toRad = Math.PI / 180;
    const dLat = (b[1] - a[1]) * toRad;
    const dLon = (b[0] - a[0]) * toRad;
    const lat1 = a[1] * toRad;
    const lat2 = b[1] * toRad;
    const x = Math.sin(dLon) * Math.cos(lat2);
    const y = Math.cos(lat1) * Math.sin(lat2) - Math.sin(lat1) * Math.cos(lat2) * Math.cos(dLon);
    return Math.atan2(Math.sqrt(x * x + y * y), Math.sin(lat1) * Math.sin(lat2) + Math.cos(lat1) * Math.cos(lat2) * Math.cos(dLon)) * R;
  }

  function queryElevation(map, coord) {
    try {
      const h = map.queryTerrainElevation(coord);
      return typeof h === 'number' ? h : null;
    } catch (e) {
      return null;
    }
  }

  function ensureTerrain(map) {
    if (!map) return;
    // Only add the DEM source; do NOT enable terrain rendering, because
    // TerraDraw's adapter does not handle terrain clicks correctly.
    if (!map.getSource('terrain')) {
      try {
        map.addSource('terrain', {
          type: 'raster-dem',
          tiles: [TERRAIN_URL],
          tileSize: 256,
          maxzoom: 15,
          encoding: 'terrarium'
        });
      } catch (e) {
        console.warn('[TerraDraw] failed to add terrain source', e);
      }
    }
  }

  /* ------------------------------------------------------------------ */
  /* Cursor                                                             */
  /* ------------------------------------------------------------------ */

  function setMeasureCursor(active) {
    const map = getMap();
    if (!map) return;
    try {
      map.getCanvas().style.cursor = active ? 'crosshair' : '';
    } catch (e) {}
  }

  /* ------------------------------------------------------------------ */
  /* Measure popup (distance / area)                                    */
  /* ------------------------------------------------------------------ */

  function removeMeasurePopup() {
    if (measurePopup) {
      measurePopup.remove();
      measurePopup = null;
    }
  }

  function updateMeasurePopup() {
    const map = getMap();
    if (!map || !measureAdded || !measureControl) return;

    const turf = getTurf();
    const fc = measureControl.getFeatures();
    if (!fc || !Array.isArray(fc.features) || !fc.features.length) {
      removeMeasurePopup();
      return;
    }

    let text = '';
    let anchor = null;

    // Prefer the line being drawn; fall back to polygon.
    const line = fc.features.find(function(f) { return f && f.geometry && f.geometry.type === 'LineString'; });
    const polygon = fc.features.find(function(f) { return f && f.geometry && f.geometry.type === 'Polygon'; });

    if (line && line.geometry.coordinates.length >= 2) {
      const coords = line.geometry.coordinates;
      if (turf && turf.length) {
        text = formatDistance(turf.length(turf.lineString(coords), { units: 'kilometers' }));
      } else {
        let meters = 0;
        for (let i = 1; i < coords.length; i++) {
          meters += segmentDistance(coords[i - 1], coords[i]);
        }
        text = formatDistance(meters / 1000);
      }
      anchor = coords[coords.length - 1];
    } else if (polygon && polygon.geometry.coordinates[0].length >= 4) {
      const ring = polygon.geometry.coordinates[0];
      if (turf && turf.area) {
        text = formatArea(turf.area(polygon));
      } else {
        // Fallback shoelace formula.
        let a = 0;
        for (let i = 0; i < ring.length - 1; i++) {
          a += ring[i][0] * ring[i + 1][1] - ring[i + 1][0] * ring[i][1];
        }
        text = formatArea(Math.abs(a) * 0.5);
      }
      // Anchor on the last drawn vertex.
      anchor = ring[ring.length - 2];
    }

    if (!text || !anchor) {
      removeMeasurePopup();
      return;
    }

    if (!measurePopup) {
      measurePopup = new maplibregl.Popup({
        closeButton: false,
        closeOnClick: false,
        offset: 8,
        className: 'measure-tooltip'
      });
    }
    measurePopup.setLngLat(anchor).setHTML(text).addTo(map);
  }

  function bindMeasureEvents(map) {
    if (!measureControl) return;
    const draw = measureControl.getTerraDrawInstance();
    if (!draw || typeof draw.on !== 'function') return;

    draw.on('change', function() { updateMeasurePopup(); });
    draw.on('finish', function() { updateMeasurePopup(); });

    // Also update on mouse move so the popup follows the cursor even when
    // TerraDraw's 'change' event does not fire for provisional coordinates.
    map.on('mousemove', function() {
      if (measureAdded) updateMeasurePopup();
    });
  }

  /* ------------------------------------------------------------------ */
  /* Sketch labels (segment distances + relative heights)               */
  /* ------------------------------------------------------------------ */

  function ensureLabelsSource(map) {
    if (map.getSource(SKETCH_LABELS_SOURCE)) return;
    map.addSource(SKETCH_LABELS_SOURCE, {
      type: 'geojson',
      data: { type: 'FeatureCollection', features: [] }
    });
    map.addLayer({
      id: SKETCH_LABELS_LAYER,
      type: 'symbol',
      source: SKETCH_LABELS_SOURCE,
      layout: {
        'text-field': ['get', 'text'],
        'text-size': 12,
        'text-offset': [0.6, -0.6],
        'text-anchor': 'left',
        'text-allow-overlap': true,
        'text-ignore-placement': true
      },
      paint: {
        'text-color': '#232E3D',
        'text-halo-color': '#ffffff',
        'text-halo-width': 2
      }
    });
  }

  function clearLabels(map) {
    const src = map.getSource(SKETCH_LABELS_SOURCE);
    if (src) src.setData({ type: 'FeatureCollection', features: [] });
  }

  function updateSketchLabels(map) {
    if (!map || !sketchControl) return;
    const fc = sketchControl.getFeatures();
    if (!fc || !Array.isArray(fc.features)) return;

    const features = [];
    fc.features.forEach(function(feature) {
      if (!feature || feature.geometry.type !== 'Polygon') return;
      const ring = feature.geometry.coordinates[0];
      if (!ring || ring.length < 2) return;
      // Ignore closing duplicate coordinate.
      const points = (ring[ring.length - 1][0] === ring[0][0] && ring[ring.length - 1][1] === ring[0][1])
        ? ring.slice(0, -1)
        : ring.slice();

      const firstElev = queryElevation(map, points[0]) || 0;

      points.forEach(function(pt, i) {
        const seg = i > 0 ? segmentDistance(points[i - 1], pt) : 0;
        const elev = queryElevation(map, pt);
        const relElev = typeof elev === 'number' ? elev - firstElev : null;

        let text = i === 0 ? '0 м' : '+' + seg.toFixed(2) + ' м';
        if (typeof relElev === 'number') {
          text += '\n' + (relElev >= 0 ? '+' : '') + relElev.toFixed(1) + ' м';
        }

        features.push({
          type: 'Feature',
          properties: { text: text },
          geometry: { type: 'Point', coordinates: pt }
        });
      });
    });

    ensureLabelsSource(map);
    const src = map.getSource(SKETCH_LABELS_SOURCE);
    if (src) src.setData({ type: 'FeatureCollection', features: features });
  }

  function bindSketchEvents(map) {
    if (!sketchControl) return;
    const draw = sketchControl.getTerraDrawInstance();
    if (!draw || typeof draw.on !== 'function') return;

    draw.on('change', function() { updateSketchLabels(map); });
    draw.on('finish', function() { updateSketchLabels(map); });
  }

  /* ------------------------------------------------------------------ */
  /* Control creation                                                   */
  /* ------------------------------------------------------------------ */

  function createControls(map) {
    if (!window.MaplibreTerradrawControl) {
      console.error('[TerraDraw] MaplibreTerradrawControl is not loaded');
      return false;
    }
    const Terradraw = window.MaplibreTerradrawControl;

    if (!measureControl && typeof Terradraw === 'function') {
      try {
        measureControl = new Terradraw({
          modes: ['render', 'linestring', 'polygon', 'select', 'delete-selection', 'delete'],
          open: true,
          showDeleteConfirmation: true
        });
        console.log('[TerraDraw] measure control created');
        bindMeasureEvents(map);
      } catch (err) {
        console.error('[TerraDraw] failed to create measure control', err);
        alert('TerraDraw measure init error: ' + (err && err.message ? err.message : err));
      }
    }

    if (!sketchControl && typeof Terradraw === 'function') {
      try {
        sketchControl = new Terradraw({
          modes: ['render', 'polygon', 'select', 'delete-selection', 'delete'],
          open: true,
          showDeleteConfirmation: true
        });
        console.log('[TerraDraw] sketch control created');
        bindSketchEvents(map);
      } catch (err) {
        console.error('[TerraDraw] failed to create sketch control', err);
        alert('TerraDraw sketch init error: ' + (err && err.message ? err.message : err));
      }
    }

    window.goszemlyaTerradrawDebug = { measureControl: measureControl, sketchControl: sketchControl };
  }

  function closeOtherTool(except) {
    const map = getMap();
    if (!map) return;
    if (except !== 'measure' && measureAdded && measureControl) {
      try { map.removeControl(measureControl); } catch (e) {}
      measureAdded = false;
      removeMeasurePopup();
    }
    if (except !== 'sketch' && sketchAdded && sketchControl) {
      try { map.removeControl(sketchControl); } catch (e) {}
      sketchAdded = false;
      clearLabels(map);
    }
  }

  function doToggleMeasure(map) {
    if (!measureControl) {
      console.warn('[TerraDraw] measure toggle unavailable', { map: !!map, measureControl: !!measureControl });
      return;
    }
    try {
      if (measureAdded) {
        map.removeControl(measureControl);
        removeMeasurePopup();
        measureAdded = false;
        setMeasureCursor(false);
      } else {
        closeOtherTool('measure');
        ensureTerrain(map);
        map.addControl(measureControl, 'top-left');
        measureAdded = true;
        setMeasureCursor(true);
        const draw = measureControl.getTerraDrawInstance();
        if (draw && typeof draw.setMode === 'function') {
          try { draw.setMode('linestring'); } catch (e) {}
        }
      }
    } catch (err) {
      console.error('[TerraDraw] toggle measure error', err);
      alert('TerraDraw measure error: ' + (err && err.message ? err.message : err));
    }
    updateButtonState();
  }

  window.toggleTerraDrawMeasure = function() {
    const map = getMap();
    if (!map) return;
    ensureTerraDrawLoaded()
      .then(function() {
        if (!measureControl) createControls(map);
        doToggleMeasure(map);
      })
      .catch(function(err) {
        console.error('[TerraDraw] lazy load error', err);
        alert('Не удалось загрузить модуль рисования. Проверьте соединение.');
      });
  };

  function doToggleSketch(map) {
    if (!sketchControl) {
      console.warn('[TerraDraw] sketch toggle unavailable', { map: !!map, sketchControl: !!sketchControl });
      return;
    }
    try {
      if (sketchAdded) {
        map.removeControl(sketchControl);
        clearLabels(map);
        sketchAdded = false;
        setMeasureCursor(false);
      } else {
        closeOtherTool('sketch');
        ensureTerrain(map);
        map.addControl(sketchControl, 'top-left');
        sketchAdded = true;
        setMeasureCursor(true);
        const draw = sketchControl.getTerraDrawInstance();
        if (draw && typeof draw.setMode === 'function') {
          try { draw.setMode('polygon'); } catch (e) {}
        }
      }
    } catch (err) {
      console.error('[TerraDraw] toggle sketch error', err);
      alert('TerraDraw sketch error: ' + (err && err.message ? err.message : err));
    }
    updateButtonState();
  }

  window.toggleTerraDrawSketch = function() {
    const map = getMap();
    if (!map) return;
    ensureTerraDrawLoaded()
      .then(function() {
        if (!sketchControl) createControls(map);
        doToggleSketch(map);
      })
      .catch(function(err) {
        console.error('[TerraDraw] lazy load error', err);
        alert('Не удалось загрузить модуль рисования. Проверьте соединение.');
      });
  };

  function updateButtonState() {
    document.querySelectorAll('.menu-item-secondary').forEach(function(btn) {
      const tool = btn.dataset.tool;
      if (tool === 'terradraw-measure') btn.classList.toggle('active', measureAdded);
      else if (tool === 'terradraw-sketch') btn.classList.toggle('active', sketchAdded);
    });
  }

  function initButtons() {
    document.querySelectorAll('.menu-item-secondary[data-tool="terradraw-measure"]').forEach(function(btn) {
      if (btn.dataset.terradrawBound) return;
      btn.dataset.terradrawBound = '1';
      btn.addEventListener('click', function(e) {
        e.preventDefault();
        e.stopPropagation();
        window.toggleTerraDrawMeasure();
      });
    });
    document.querySelectorAll('.menu-item-secondary[data-tool="terradraw-sketch"]').forEach(function(btn) {
      if (btn.dataset.terradrawBound) return;
      btn.dataset.terradrawBound = '1';
      btn.addEventListener('click', function(e) {
        e.preventDefault();
        e.stopPropagation();
        window.toggleTerraDrawSketch();
      });
    });
  }

  function init(map) {
    console.log('[TerraDraw] init', { hasMap: !!map, hasTerradraw: !!window.MaplibreTerradrawControl });
    initButtons();
  }

  function tryInit() {
    const map = getMap();
    if (map) init(map);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function() {
      tryInit();
      window.addEventListener('map:ready', function(e) {
        init(e.detail && e.detail.map ? e.detail.map : window.map);
      });
    });
  } else {
    tryInit();
    window.addEventListener('map:ready', function(e) {
      init(e.detail && e.detail.map ? e.detail.map : window.map);
    });
  }
})();
